import React, { useState } from 'react';
import { CommonButton } from 'components/Buttons';
import { DetailCategory } from 'models/response';
import { Wrapper, Heading } from './style';
import utils from 'utils';
import { SearchParams, ViewMode, AreaWithGroupedKpis } from 'routes/ControlTower/interfaces';
import { CategoryDetailsFilters, KpiCategoryFilterConfiguration, KpiCategoryFilterValue } from 'models/request';
import { createFiltersObject } from 'routes/ControlTower/utils';
import styled from 'styled-components/macro';
import TableView from './TableView';
import ListView from './ListView';
import { colorPalette } from 'utils/styleConstants';
import { useLocationParams } from 'routes/ControlTower/hooks';
import CategoryFilters from 'routes/ControlTower/Components/CategoryFilters';

interface DetailCategoriesProps {
  detailedCategories: DetailCategory[];
  hierarchicalCategories: DetailCategory[];
  searchParams?: SearchParams;
  exportReport: (reportId: number, kpiCategoryFilterValues?: KpiCategoryFilterValue[]) => Promise<void>;
  viewReport: (reportId: number, kpiCategoryFilterValues?: KpiCategoryFilterValue[]) => Promise<void>;
  loadCategoryDetails: (params: CategoryDetailsFilters) => Promise<void>;
  setViewMode: (value: ViewMode) => void;
  viewMode: ViewMode;
  drillDownProtocols: (
    idMetric: number,
    idGeoList: any,
    kpiCategoryFilterValues?: KpiCategoryFilterValue[],
  ) => Promise<void>;
  message: string;
  onResetCategoryFilters?: (resetFn: () => void) => void;
}

const HeadingContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

export const SEMAPHORE_COLORS = {
  GREEN: colorPalette.green,
  RED: colorPalette.red,
};
const DetailCategories: React.FC<DetailCategoriesProps> = ({
  detailedCategories,
  hierarchicalCategories,
  searchParams,
  exportReport,
  viewReport,
  loadCategoryDetails,
  setViewMode,
  viewMode,
  drillDownProtocols,
  message,
  onResetCategoryFilters,
}) => {
  const t = utils.intl.useTranslator();
  const { categoryId } = useLocationParams();

  const [activeCategoryFilters, setActiveCategoryFilters] = useState<KpiCategoryFilterValue[]>([]);
  const [categoryFilterConfig, setCategoryFilterConfig] = useState<KpiCategoryFilterConfiguration[]>([]);
  const [loadedFilters, setLoadedFilters] = useState<{ table?: string; list?: string }>({});

  const getFiltersKey = (filters: KpiCategoryFilterValue[]) => JSON.stringify(filters);

  const tableData = detailedCategories.find(({ idCategory }) => idCategory === categoryId);
  const listData = hierarchicalCategories.find(({ idCategory }) => idCategory === categoryId);
  const categoryData = viewMode === 'list' ? listData : tableData;
  const { areas, name: categoryName, unifyHeaders } = categoryData || {};

  const hasAnyData = detailedCategories.length > 0 || hierarchicalCategories.length > 0;

  const { dateTo, dateFrom, gbsList, channels, companies, types } = searchParams || {};
  const params = createFiltersObject(searchParams);

  const handleFiltersChange = async (kpiCategoryFilterValues: KpiCategoryFilterValue[]) => {
    setLoadedFilters({});
    await loadCategoryDetails({
      ...params,
      idCategory: categoryId!,
      allInOneView: viewMode === 'table',
      kpiCategoryFilterValues,
    });
    setActiveCategoryFilters(kpiCategoryFilterValues);
    setLoadedFilters((prev) => ({ ...prev, [viewMode]: getFiltersKey(kpiCategoryFilterValues) }));
  };

  const handleCategoryFiltersReset = (resetFn: () => void) => {
    onResetCategoryFilters?.(() => {
      resetFn();
      setActiveCategoryFilters([]);
    });
  };

  const exportReportWithCategoryFilters = (idReport: number) => exportReport(idReport, activeCategoryFilters);
  const viewReportWithCategoryFilters = (idReport: number) => viewReport(idReport, activeCategoryFilters);
  const drillDownProtocolsWithCategoryFilters = (idMetric: number, idGeoList: any) =>
    drillDownProtocols(idMetric, idGeoList, activeCategoryFilters);

  if (!categoryId) return <></>;

  return (
    <>
      {!hasAnyData && message && <Wrapper>{message}</Wrapper>}

      {hasAnyData && (
        <Wrapper>
          <HeadingContainer>
            <Heading>{categoryName}</Heading>
            <CommonButton
              id="ct-switch-view-mode"
              scope="tertiary"
              action={() => {
                const nextView: ViewMode = viewMode === 'list' ? 'table' : 'list';
                const nextViewLoaded = loadedFilters[nextView] === getFiltersKey(activeCategoryFilters);

                if (!nextViewLoaded) {
                  loadCategoryDetails({
                    ...params,
                    idCategory: categoryId,
                    allInOneView: nextView === 'table',
                    kpiCategoryFilterValues: activeCategoryFilters,
                  });
                  setLoadedFilters((prev) => ({ ...prev, [nextView]: getFiltersKey(activeCategoryFilters) }));
                }

                setViewMode(nextView);
              }}
              value={
                viewMode === 'list'
                  ? utils.app.textTranslated('Switch to table view', t)
                  : utils.app.textTranslated('Switch to list view', t)
              }
            />
          </HeadingContainer>

          <CategoryFilters
            categoryId={categoryId}
            onFiltersChange={handleFiltersChange}
            onReset={handleCategoryFiltersReset}
            onConfigLoad={setCategoryFilterConfig}
          />

          {viewMode === 'table' && (
            <TableView
              dateTo={dateTo}
              dateFrom={dateFrom}
              gbsList={gbsList?.map(({ label }) => label)}
              companies={companies?.map(({ label }) => label)}
              channels={channels?.map(({ label }) => label)}
              types={types?.map(({ label }) => label)}
              categoryName={categoryName}
              areas={areas}
              unifyHeaders={unifyHeaders}
              kpiCategoryFilterValues={activeCategoryFilters}
              kpiCategoryFilterConfig={categoryFilterConfig}
            />
          )}

          {viewMode === 'list' && (
            <ListView
              dateTo={dateTo}
              dateFrom={dateFrom}
              gbsList={gbsList?.map(({ label }) => label)}
              companies={companies?.map(({ label }) => label)}
              channels={channels?.map(({ label }) => label)}
              types={types?.map(({ label }) => label)}
              categoryName={categoryName}
              areas={areas as AreaWithGroupedKpis[]}
              categoryId={categoryId}
              exportReport={exportReportWithCategoryFilters}
              viewReport={viewReportWithCategoryFilters}
              drillDownProtocols={drillDownProtocolsWithCategoryFilters}
              kpiCategoryFilterValues={activeCategoryFilters}
              kpiCategoryFilterConfig={categoryFilterConfig}
            />
          )}
        </Wrapper>
      )}
    </>
  );
};

export default DetailCategories;
